import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatElapsedSince } from '@/lib/format';
import { renderNotification } from '@/lib/notification-labels';
import { EmptyState } from '@/components/EmptyState';

/** `/notifications` (v0.4 §7 通知基盤 L-1)。自分宛の通知の全件一覧 (既読含む)。
 *  権限は `notification:view` self のみ (全ロールに seed 済み)、追加のゲートは不要。 */

type NotificationItem = {
  recipient_id: string;
  notification_id: string;
  event_type_id: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
};

type NotificationsResponse = { notifications: NotificationItem[]; unread_count: number };

export function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<NotificationsResponse>({
    queryKey: ['notifications', { unread: unreadOnly }],
    queryFn: () => apiFetch<NotificationsResponse>(`/notifications${unreadOnly ? '?unread=1' : ''}`),
  });

  const readMut = useMutation({
    mutationFn: (notificationId: string) => apiFetch(`/notifications/${notificationId}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = data?.notifications ?? [];

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-h1">通知</h1>
          <p className="text-sm text-text-sub mt-1">シフト・承認・公開・AI利用量などの通知一覧です</p>
        </div>
        <label className="flex items-center gap-2 text-sm min-h-[44px] shrink-0">
          <input
            type="checkbox"
            className="w-5 h-5 accent-brand-red"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          未読のみ表示
        </label>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : items.length === 0 ? (
        <EmptyState
          message={unreadOnly ? '未読の通知はありません' : '通知はまだありません'}
          description={unreadOnly ? undefined : 'シフトの確定・締切のリマインド・承認依頼などがここに表示されます。'}
        />
      ) : (
        <ul className="border border-surface-border rounded-lg bg-white divide-y divide-surface-line overflow-hidden">
          {items.map((item) => {
            const rendered = renderNotification(item.event_type_id, item.payload);
            const isUnread = !item.read_at;
            return (
              <li key={item.recipient_id} className={isUnread ? 'bg-misespark-navy/[0.06]' : undefined}>
                <button
                  type="button"
                  disabled={!isUnread}
                  onClick={() => isUnread && readMut.mutate(item.notification_id)}
                  className={`w-full text-left px-4 py-3 min-h-[44px] flex items-start gap-3 ${isUnread ? 'hover:bg-surface-sunken cursor-pointer' : 'cursor-default'}`}
                >
                  <span
                    aria-hidden
                    className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${isUnread ? 'bg-brand-red' : 'bg-transparent'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm break-words ${isUnread ? 'font-semibold text-text-body' : 'text-text-sub'}`}>
                      {rendered.title}
                    </span>
                    <span className="block text-sm text-text-sub break-words mt-0.5">{rendered.body}</span>
                    <span className="block text-xs text-text-muted mt-1">{formatElapsedSince(item.created_at)}</span>
                  </span>
                  {isUnread && (
                    <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
                      未読
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!isLoading && items.length === 0 && unreadOnly && (
        <div className="flex items-center gap-2 text-xs text-text-sub">
          <BellOff size={14} aria-hidden />
          「未読のみ表示」をオフにするとすべての通知が確認できます
        </div>
      )}
    </div>
  );
}
